// RevenueCat (Apple In-App Purchases) — iOS ONLY.
// Web & Android keep using Stripe; every function here is a no-op off iOS.
import { Capacitor } from '@capacitor/core';
import { Purchases } from '@revenuecat/purchases-capacitor';
import { Browser } from '@capacitor/browser';
import { REVENUECAT_IOS_PUBLIC_KEY } from './config';

export function isIOS() {
  return Capacitor.getPlatform() === 'ios';
}

// Map App Store product IDs -> internal plan keys (must match backend).
export const REVENUECAT_PRODUCT_TO_PLAN = {
  'com.guestcheckout.app.starter.monthly': 'starter',
  'com.guestcheckout.app.starter.yearly': 'starter',
  'com.guestcheckout.app.growth.monthly': 'growth',
  'com.guestcheckout.app.growth.yearly': 'growth',
  'com.guestcheckout.app.professional.monthly': 'professional',
  'com.guestcheckout.app.professional.yearly': 'professional',
  'com.guestcheckout.app.business.monthly': 'business',
  'com.guestcheckout.app.business.yearly': 'business',
  'com.guestcheckout.app.enterprise.monthly': 'enterprise',
  'com.guestcheckout.app.enterprise.yearly': 'enterprise',
};

let _configured = false;

// Configure once, after login. appUserID = companyId (subscription belongs to the company).
export async function configureRevenueCat(appUserID) {
  if (!isIOS() || _configured || !appUserID) return false;
  try { await Purchases.setLogLevel({ level: 'DEBUG' }); } catch (e) { /* older plugin */ }
  await Purchases.configure({
    apiKey: REVENUECAT_IOS_PUBLIC_KEY,
    appUserID: String(appUserID),
  });
  _configured = true;
  return true;
}

// Diagnostic helper (iOS only) — surfaces exactly what RevenueCat/StoreKit returns,
// so we can see WHY packages may be empty (offering config vs StoreKit product resolution).
export async function getIOSDiagnostics() {
  if (!isIOS()) return { ios: false };
  const diag = { configured: _configured, keyPresent: !!REVENUECAT_IOS_PUBLIC_KEY };
  try {
    const offerings = await Purchases.getOfferings();
    const all = offerings?.all ?? {};
    diag.currentOffering = offerings?.current?.identifier ?? null;
    diag.currentPackages = offerings?.current?.availablePackages?.length ?? 0;
    diag.allOfferings = Object.keys(all).map((k) => `${k}=${(all[k]?.availablePackages || []).length}`);
    const ids = [];
    for (const k of Object.keys(all)) {
      for (const p of all[k]?.availablePackages || []) ids.push(p?.product?.identifier);
    }
    diag.productIds = ids;
  } catch (e) {
    diag.error = String(e?.message || e);
  }
  return diag;
}

// Returns available packages. Prefers the "Current" offering, but falls back to
// collecting packages from ALL offerings so the app works regardless of which
// offering is marked Current in RevenueCat.
export async function getIOSPackages() {
  if (!isIOS()) return [];
  const offerings = await Purchases.getOfferings();
  const fromCurrent = offerings?.current?.availablePackages ?? [];
  if (fromCurrent.length) return fromCurrent;
  const all = offerings?.all ?? {};
  const collected = [];
  for (const key of Object.keys(all)) {
    for (const pkg of all[key]?.availablePackages ?? []) collected.push(pkg);
  }
  return collected;
}

// Buy a package object (not a product id). Returns { customerInfo } on success.
export async function buyIOSPackage(aPackage) {
  if (!isIOS()) throw new Error('RevenueCat is iOS-only');
  return Purchases.purchasePackage({ aPackage });
}

export async function restoreIOSPurchases() {
  if (!isIOS()) return null;
  return Purchases.restorePurchases();
}

export async function currentIOSCustomerInfo() {
  if (!isIOS()) return null;
  const { customerInfo } = await Purchases.getCustomerInfo();
  return customerInfo;
}

// Opens Apple's subscription-management page.
export async function showManageSubscriptions() {
  if (!isIOS()) return;
  const { customerInfo } = await Purchases.getCustomerInfo();
  const url = customerInfo?.managementURL;
  if (url) {
    await Browser.open({ url });
  }
}

// Helper: find the package for a given plan + billing cycle ('month'|'year').
export function findPackage(packages, plan, cycle = 'month') {
  const suffix = cycle === 'year' ? '.yearly' : '.monthly';
  return packages.find((p) => {
    const pid = p?.product?.identifier || '';
    return REVENUECAT_PRODUCT_TO_PLAN[pid] === plan && pid.endsWith(suffix);
  });
}

// Helper: Apple-localized formatted price string for a plan ('£24.99'), or null.
export function priceStringForPlan(packages, plan, cycle = 'month') {
  const pkg = findPackage(packages, plan, cycle);
  return pkg?.product?.priceString || null;
}
